import React, { useEffect } from "react";
import Button from "@/components/buttons/Button";
import FormActions from "@/components/buttons/FormActions";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  processing?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  processing = false,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleOverlayClick}
    >
      <div className="dark:bg-cream-900 mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="font-sans text-xl font-bold">{title}</h2>
        <p className="text-cream-700 dark:text-cream-400 mt-2">{message}</p>
        <FormActions className="mt-6">
          <Button
            text="Cancel"
            type="tertiary"
            htmlButtonType="button"
            onClick={onClose}
            disabled={processing}
          />
          <Button
            text={processing ? "Deleting..." : confirmText}
            type="danger-confirm"
            htmlButtonType="button"
            onClick={onConfirm}
            disabled={processing}
          />
        </FormActions>
      </div>
    </div>
  );
};

export default ConfirmDialog;
